/**
 * The Overview's read models (wave 10): `/api/v1/overview/kpis`, `/revenue-trend`
 * and `/top-competitions` (`modules/overview`), `/overview/funnel`
 * (`modules/funnel`) and `/overview/channel-performance` (`modules/channel_summary`),
 * plus the shell's entitlements from `/tenancy/feature-flags`. Each module's
 * README is the contract; this file is the one place their wire shapes become
 * the app's.
 *
 * Every figure the API cannot give arrives as `null` and stays `null` here, so
 * a screen shows "Not live yet" or "—" for it, never 0. Money arrives in
 * integer minor units of `meta.currency` and leaves in major units of the same
 * currency, which the envelope's `meta.currency` names.
 */

import { majorUnits } from '@/design/format'
import { dayCount, formatDateKey, formatWindow, resolvePeriod, type GlobalFilters } from '@/lib/filters'

import {
  FUNNEL_STAGES,
  KPI_KEYS,
  KPI_PRESENTATION,
  MODULE_KEYS,
  type ChannelPerformance,
  type CompetitionPerformance,
  type DataMeta,
  type Entitlements,
  type Environment,
  type FunnelStage,
  type FunnelStageFigures,
  type FunnelStageKey,
  type Kpi,
  type KpiKey,
  type KpiUnavailableReason,
  type MeasurementCoverage,
  type ModuleKey,
  type RevenueTrend,
  type TrendWindow,
} from './types'

// --- wire shapes (snake_case, as the API sends them) -------------------------

interface DayRangeOut {
  from: string
  to: string
}

/** `modules/overview/schemas.py` `Meta`: one shape on all three routes. */
export interface OverviewMetaOut {
  currency: string
  timezone: string
  range: DayRangeOut
  /** Null on `top-competitions`, which makes no comparison. */
  previous_range: DayRangeOut | null
  previous_unavailable: 'currency_rate_unavailable' | null
  freshness: { latest_source_updated_at: string | null }
  source: 'live'
  environments: Environment[]
  non_production: boolean
  website_id: string | null
}

export type KpiUnitOut = 'currency_minor' | 'count' | 'ratio'
export type KpiNotAvailableOut = 'needs_browser_events' | 'needs_ad_platform'
/** Why a served ratio's `current` is null: its denominator is zero (the README's *Advertising KPIs*). */
export type KpiNullReasonOut = 'no_spend' | 'no_attributed_purchases'

export interface KpiOut {
  unit: KpiUnitOut
  current: number | null
  previous: number | null
  delta_pct: number | null
  /** The API always sends it; the README's example leaves it out, as do the fixtures copied from it. */
  null_reason?: KpiNullReasonOut | null
}

export interface KpisOut {
  data: {
    kpis: Record<string, KpiOut>
    not_available: Record<string, KpiNotAvailableOut>
    headline_revenue: string
  }
  meta: OverviewMetaOut
}

export interface TrendPointOut {
  date: string
  end_date: string
  revenue_gross_minor: number
  revenue_net_minor: number
  orders: number
}

interface TrendTotalsOut {
  revenue_gross_minor: number
  revenue_net_minor: number
  orders: number
}

export interface RevenueTrendOut {
  data: {
    granularity: 'day' | 'week'
    points: TrendPointOut[]
    previous_points: TrendPointOut[]
    totals: TrendTotalsOut
    /** Null, with `previous_points` empty, when `meta.previous_unavailable` says why. */
    previous_totals: TrendTotalsOut | null
  }
  meta: OverviewMetaOut
}

export interface CompetitionRowOut {
  competition_id: string
  external_competition_id: string
  website_id: string
  name: string
  status: string
  active: boolean
  category: string | null
  end_at: string | null
  draw_at: string | null
  ticket_price_minor: number | null
  ticket_price_currency: string
  revenue_minor: number
  tickets_sold_in_range: number
  tickets_sold_lifetime: number | null
  total_ticket_capacity: number | null
  sell_through: number | null
  orders: number
  image_url: string | null
  product_url: string | null
}

export interface TopCompetitionsOut {
  data: {
    sort: 'revenue' | 'tickets' | 'sell_through'
    limit: number
    rows: CompetitionRowOut[]
    by_status: { status: string; count: number; active: boolean }[]
  }
  meta: OverviewMetaOut
}

interface DrillDownOut {
  page: string
  params: Record<string, string>
}

interface StageMetricsOut {
  users: number
  sessions: number
  stage_conversion_rate: number | null
  total_conversion_rate: number | null
  drop_off: number | null
  drop_off_rate: number | null
}

export interface FunnelStageOut extends StageMetricsOut {
  stage: FunnelStageKey
  label: string
  previous: StageMetricsOut
  drill_down: DrillDownOut
}

export interface FunnelOut {
  stages: FunnelStageOut[]
  coverage: {
    first_event_at: string | null
    sessions_measured: number
    orders: number
    orders_with_session: number
    orders_with_session_share: number | null
    stages_without_events: FunnelStageKey[]
    note: string
  }
  meta: {
    range: DayRangeOut
    previous_range: DayRangeOut
    source: 'live'
    measurement: 'measured_sessions'
    environments: Environment[]
    non_production: boolean
    website_id: string | null
  }
}

interface ChannelMetricsOut {
  spend_minor: number | null
  attributed_revenue_minor: number
  orders: number
  cpa_minor: number | null
  roas: number | null
}

export interface ChannelRowOut extends ChannelMetricsOut {
  entity: { type: 'campaign_channel' | 'channel'; id: string; label: string }
  kind: 'digital' | 'offline' | null
  previous: ChannelMetricsOut
  drill_down: DrillDownOut
}

export interface ChannelPerformanceOut {
  rows: ChannelRowOut[]
  totals: ChannelMetricsOut
  totals_previous: ChannelMetricsOut
  unattributed: {
    attributed_revenue_minor: number
    orders: number
    previous_attributed_revenue_minor: number
    previous_orders: number
  }
  meta: {
    range: DayRangeOut
    previous_range: DayRangeOut
    model: string
    currency: string
    environments: Environment[]
    non_production: boolean
    website_id: string | null
    source: 'attributed_conversion_daily'
    spend_source: 'ad_insights_daily'
  }
}

/** `modules/tenancy/schemas.py` `FeatureFlagsOut`. */
export interface FeatureFlagsOut {
  flags: { key: string; description: string; enabled: boolean; default: boolean; overridden: boolean }[]
  navigation: string[]
}

// --- money ---------------------------------------------------------------------

function majorOrNull(minor: number | null, currency: string): number | null {
  return minor === null ? null : majorUnits(minor, currency)
}

// --- query strings -------------------------------------------------------------

/**
 * `from`/`to` (tenant days, inclusive), `website_id` and the environments, as
 * every Overview route reads them. The period is always sent, so the API's
 * own 28-day default never applies to a page showing another one.
 */
function scopeParams(filters: GlobalFilters, now: Date): URLSearchParams {
  const { current } = resolvePeriod(filters, now)
  const params = new URLSearchParams({ from: current.from, to: current.to })
  const website = filters.dimensions.website
  if (website) params.set('website_id', website)
  // Empty means the production default, decided by the server (as `withEnvironments`).
  for (const environment of filters.environments) params.append('environment', environment)
  return params
}

export function overviewSearch(filters: GlobalFilters, now: Date): string {
  return scopeParams(filters, now).toString()
}

/**
 * The trend card's window replaces the global date range, as the card says
 * (and as the demo does); every other filter still applies. The API has no
 * hourly buckets, so `today` is today alone at `day` granularity, and 7d / 30d
 * are the last 7 / 30 days, today included.
 */
export function trendFilters(filters: GlobalFilters, window: TrendWindow): GlobalFilters {
  return { ...filters, range: window, from: null, to: null }
}

export function revenueTrendSearch(filters: GlobalFilters, window: TrendWindow, now: Date): string {
  const params = scopeParams(trendFilters(filters, window), now)
  params.set('granularity', 'day')
  return params.toString()
}

/** The API's `limit` is 1–50. */
const MAX_TOP_COMPETITIONS = 50

export function topCompetitionsSearch(filters: GlobalFilters, limit: number, now: Date): string {
  const params = scopeParams(filters, now)
  params.set('sort', 'revenue')
  params.set('limit', String(Math.min(MAX_TOP_COMPETITIONS, Math.max(1, Math.trunc(limit)))))
  return params.toString()
}

/** The channel table reads the default attribution model, named so a server default change cannot move it silently. */
export const CHANNEL_PERFORMANCE_MODEL = 'last_non_direct'

export function channelPerformanceSearch(filters: GlobalFilters, now: Date): string {
  const params = scopeParams(filters, now)
  params.set('model', CHANNEL_PERFORMANCE_MODEL)
  return params.toString()
}

// --- meta ------------------------------------------------------------------------

/** What the four route metas share, enough to say which period the figures cover. */
interface RangesOut {
  range: DayRangeOut
  previous_range: DayRangeOut | null
}

/**
 * The envelope's provenance: live, the period the API actually computed (its
 * tenant days), and the comparison it made. The API's previous period is
 * always the same number of days just before `from`; when the filters asked
 * for another (last year, the calendar month before) the label names the
 * period compared rather than the one asked for, and with comparison off
 * there is none.
 */
/** What a route says about its own figures, beyond their period: every call site states all three, null where its route has none. */
export interface OverviewProvenance {
  lastUpdatedAt: string | null
  currency: string | null
  coverage: MeasurementCoverage | null
}

export function overviewMeta(wire: RangesOut, filters: GlobalFilters, now: Date, provenance: OverviewProvenance): DataMeta {
  const resolved = resolvePeriod(filters, now)
  const previous = comparing(filters) ? wire.previous_range : null
  const asked = resolved.previous
  const compareLabel = !previous
    ? null
    : asked && asked.from === previous.from && asked.to === previous.to
      ? resolved.compareLabel
      : `vs ${formatWindow({ ...previous, days: dayCount(previous.from, previous.to) })}`
  const { lastUpdatedAt, currency, coverage } = provenance
  return {
    source: 'live',
    lastUpdatedAt,
    ...(currency ? { currency } : {}),
    ...(coverage ? { coverage } : {}),
    period: {
      from: wire.range.from,
      to: wire.range.to,
      label: resolved.label,
      compareLabel,
      previousFrom: previous?.from ?? null,
      previousTo: previous?.to ?? null,
    },
  }
}

function comparing(filters: GlobalFilters): boolean {
  return filters.compare !== 'none'
}

// --- KPIs ------------------------------------------------------------------------

/**
 * Where each tile's figure comes from on `/overview/kpis` (its README's
 * "Mapping to the web app's `KPI_KEYS`"), or why there is none. `revenue`
 * follows `headline_revenue` instead.
 */
const KPI_SOURCES: Record<KpiKey, string | { unavailable: KpiUnavailableReason }> = {
  revenue: 'revenue_gross',
  orders: 'orders',
  tickets: 'tickets_sold',
  new_customers: 'new_customers',
  returning_customers: 'returning_customers',
  signups: 'signups',
  aov: 'aov',
  conversion_rate: 'conversion_rate',
  ad_spend: 'ad_spend',
  // Attributed *ad* revenue: the Channel performance card's revenue on rows with spend, the ROAS numerator.
  attributed_revenue: 'attributed_revenue',
  roas: 'roas',
  active_competitions: 'active_competitions',
  // No automation read model exists yet.
  automations_running: { unavailable: 'no_read_model' },
}

const SECONDARY_REVENUE: Record<string, { key: string; label: string }> = {
  revenue_gross: { key: 'revenue_net', label: 'Net after refunds' },
  revenue_net: { key: 'revenue_gross', label: 'Gross before refunds' },
}

/** What a served tile with no figure says when the API gave the reason. */
const NULL_REASON_NOTES: Record<KpiNullReasonOut, string> = {
  no_spend: 'No ad spend in the period',
  no_attributed_purchases: 'No attributed purchases',
}

/** The API's `delta_pct` is a percentage; the web's change is a fraction. */
function changeFromWire(deltaPct: number | null): number | null {
  return deltaPct === null ? null : deltaPct / 100
}

/** A KPI's figure on the wire, or why there is none. */
type KpiWire = { wire: KpiOut } | { unavailable: KpiUnavailableReason }

/** Where a tile's figure is: the headline revenue for `revenue`, its mapped key otherwise, unless the API marks it not available. */
function kpiWire(key: KpiKey, data: KpisOut['data']): KpiWire {
  const source = KPI_SOURCES[key]
  if (typeof source !== 'string') return source
  const wireKey = key === 'revenue' ? data.headline_revenue : source
  const reason = data.not_available[wireKey]
  const wire = data.kpis[wireKey]
  if (reason) return { unavailable: reason }
  return wire ? { wire } : { unavailable: 'no_read_model' }
}

/** Why a served KPI shows no change although one was asked for: nothing to compare with, or no exchange rate for it. */
function comparisonNote(wire: KpiOut, meta: OverviewMetaOut): string | undefined {
  if (wire.delta_pct !== null || wire.current === null) return undefined
  return meta.previous_unavailable && wire.previous === null ? 'No exchange rate for the previous period' : 'None in the previous period'
}

/**
 * Every tile the web shows, from `/overview/kpis`. A key in `not_available`,
 * or one no route serves, is a `null` value with its reason. With comparison
 * off there is no previous value or change; otherwise the API's own
 * `delta_pct` is the change, and no change is shown where it gave none.
 * Revenue carries the other revenue figure beside its headline.
 */
export function kpisFromWire(body: KpisOut, filters: GlobalFilters): Kpi[] {
  const { kpis, headline_revenue: headline } = body.data
  const compare = comparing(filters)
  const money = (wire: KpiOut, value: number | null) =>
    wire.unit === 'currency_minor' ? majorOrNull(value, body.meta.currency) : value

  return KPI_KEYS.map((key): Kpi => {
    const base = { key, ...KPI_PRESENTATION[key], sparkline: [] }
    const resolved = kpiWire(key, body.data)
    if ('unavailable' in resolved) {
      return { ...base, value: null, previous: null, change: null, unavailable: resolved.unavailable }
    }

    // A served KPI can still be null for the period (no orders, no AOV): "—", not "Not live yet".
    const { wire } = resolved
    const kpi: Kpi = {
      ...base,
      value: money(wire, wire.current),
      previous: compare ? money(wire, wire.previous) : null,
      change: compare ? changeFromWire(wire.delta_pct) : null,
    }
    // A null figure's own reason (ROAS with no spend) explains more than the missing change does.
    const reason = wire.current === null && wire.null_reason ? NULL_REASON_NOTES[wire.null_reason] : undefined
    const note = reason ?? (compare ? comparisonNote(wire, body.meta) : undefined)
    if (note) kpi.comparisonNote = note

    const secondary = key === 'revenue' ? SECONDARY_REVENUE[headline] : undefined
    const other = secondary && kpis[secondary.key]
    const otherValue = other ? money(other, other.current) : null
    if (secondary && otherValue !== null) kpi.secondary = { label: secondary.label, value: otherValue }
    return kpi
  })
}

// --- revenue trend ---------------------------------------------------------------

/**
 * The trend in the headline revenue (gross, as `headline_revenue` reads until
 * the revenue definition is decided), `previous_points[i]` beside `points[i]`.
 * Both are empty when neither period has an order or refund; the card shows
 * its empty state then.
 */
export function revenueTrendFromWire(body: RevenueTrendOut, window: TrendWindow, meta: DataMeta): RevenueTrend {
  const { points, previous_points: previousPoints, totals, previous_totals: previousTotals } = body.data
  const currency = body.meta.currency
  // `overviewMeta` names a comparison exactly when one was asked for; without
  // previous totals (`previous_unavailable`) there is none to draw, and the card says why.
  const asked = meta.period?.compareLabel ?? null
  const unconverted = asked !== null && previousTotals === null
  const compareLabel = unconverted ? null : asked
  return {
    window,
    granularity: 'day',
    total: majorUnits(totals.revenue_gross_minor, currency),
    previousTotal: compareLabel && previousTotals ? majorUnits(previousTotals.revenue_gross_minor, currency) : null,
    compareLabel,
    ...(unconverted ? { comparisonNote: 'No exchange rate for the previous period' } : {}),
    points: points.map((point, index) => {
      const before = compareLabel ? previousPoints[index] : undefined
      return {
        at: point.date,
        label: formatDateKey(point.date),
        current: majorUnits(point.revenue_gross_minor, currency),
        previous: before ? majorUnits(before.revenue_gross_minor, currency) : null,
      }
    }),
    drillDown: { path: '/analytics/revenue' },
  }
}

// --- top competitions ------------------------------------------------------------

/** An instant as the tenant's calendar day; the UTC day if the zone is one this browser does not know. */
export function tenantDay(at: string, timeZone: string): string {
  const instant = new Date(at)
  try {
    // `en-CA` writes dates as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant)
  } catch {
    return instant.toISOString().slice(0, 10)
  }
}

/**
 * One row per competition, 1:1. Conversion, velocity and pacing have no read
 * model yet and are `null`; sell-through is the website's own lifetime figure.
 */
export function topCompetitionsFromWire(body: TopCompetitionsOut): CompetitionPerformance[] {
  const { currency, timezone } = body.meta
  return body.data.rows.map((row) => {
    const entity = { type: 'competition' as const, id: row.competition_id, label: row.name }
    return {
      entity,
      category: row.category,
      revenue: majorUnits(row.revenue_minor, currency),
      ticketsSold: row.tickets_sold_in_range,
      conversionRate: null,
      sellThrough: row.sell_through,
      velocityPerDay: null,
      pacing: null,
      closesOn: row.end_at ? tenantDay(row.end_at, timezone) : null,
      drillDown: { path: `/analytics/competitions/${encodeURIComponent(row.competition_id)}`, entity },
    }
  })
}

// --- funnel ------------------------------------------------------------------------

function stageFigures(metrics: StageMetricsOut): FunnelStageFigures {
  return {
    users: metrics.users,
    sessions: metrics.sessions,
    stageConversionRate: metrics.stage_conversion_rate,
    totalConversionRate: metrics.total_conversion_rate,
    dropOff: metrics.drop_off,
    dropOffRate: metrics.drop_off_rate,
  }
}

/**
 * The seven stages in measured sessions, each with the previous period's
 * beside it, and the coverage block that says how much the funnel can see.
 * A stage in `stages_without_events` is unmeasured, not zero. Users, the rates
 * and the drop-off are the API's own, never recomputed here.
 */
export function funnelFromWire(body: FunnelOut, filters: GlobalFilters): { stages: FunnelStage[]; coverage: MeasurementCoverage } {
  const compare = comparing(filters)
  const unmeasured = new Set(body.coverage.stages_without_events)
  const known = new Set<string>(FUNNEL_STAGES)
  const stages = body.stages
    .filter((stage) => known.has(stage.stage))
    .map((stage) => ({
      key: stage.stage,
      label: stage.label,
      ...stageFigures(stage),
      count: stage.sessions,
      previousCount: compare ? stage.previous.sessions : null,
      previous: compare ? stageFigures(stage.previous) : null,
      measured: !unmeasured.has(stage.stage),
      drillDown: { path: stage.drill_down.page, params: stage.drill_down.params },
    }))
  const { coverage } = body
  return {
    stages,
    coverage: {
      note: coverage.note,
      firstEventAt: coverage.first_event_at,
      sessionsMeasured: coverage.sessions_measured,
      orders: coverage.orders,
      ordersWithSession: coverage.orders_with_session,
      ordersWithSessionShare: coverage.orders_with_session_share,
    },
  }
}

// --- channel performance ----------------------------------------------------------

/**
 * One row per channel, 1:1. Spend is `null` where nothing reports it (offline
 * and hand-entered channels), and so are the CPA and ROAS it would give. A
 * Suite campaign channel and a marketing channel are both the `channel`
 * entity: each row's drill-down is the API's own.
 */
export function channelPerformanceFromWire(body: ChannelPerformanceOut): ChannelPerformance[] {
  const currency = body.meta.currency
  return body.rows.map((row) => ({
    entity: { type: 'channel', id: row.entity.id, label: row.entity.label },
    kind: row.kind,
    spend: majorOrNull(row.spend_minor, currency),
    attributedRevenue: majorUnits(row.attributed_revenue_minor, currency),
    orders: row.orders,
    cpa: majorOrNull(row.cpa_minor, currency),
    roas: row.roas,
    drillDown: { path: row.drill_down.page, params: row.drill_down.params },
  }))
}

// --- shell: entitlements and freshness ----------------------------------------------

/**
 * The capability flag that gates a plan module today, where the catalogue
 * (`modules/tenancy/flags.py`) has one. A module with none is not restricted
 * by the plan yet and stays on, as production has always shown it.
 *
 * This restates part of the API's catalogue, which the web cannot import;
 * `gatedModules.test.ts` reads the catalogue (`@/test/flagCatalogue`) and fails
 * if the two drift in the one way that would open a module on a failed read.
 */
const MODULE_FLAGS: Record<ModuleKey, string | null> = {
  core_analytics: 'analytics_dashboards',
  insights: null,
  marketing_intelligence: null,
  automation: null,
  competition_operations: null,
  market_intelligence: null,
}

/**
 * Whether the plan can switch `key` off today: `MODULE_FLAGS` names a flag for
 * it. When the flags read fails, a gated module's access is unknown and stays
 * shut; an ungated one is on whatever the answer would have been. A module the
 * catalogue starts gating by a flag named after itself must be listed above
 * too, or a failed read would leave it open.
 */
export function isFlagGatedModule(key: ModuleKey): boolean {
  return MODULE_FLAGS[key] !== null
}

/**
 * The plan from the resolved flags (pivot doc: entitlements are module keys on
 * the feature-flag mechanism). A module follows a flag named after itself once
 * the catalogue has one, else its capability flag; a flag it names that the
 * answer does not carry is off. Flags this app does not know are ignored.
 */
export function entitlementsFromFlags(body: FeatureFlagsOut): Entitlements {
  const enabled = new Map(body.flags.map((flag) => [flag.key, flag.enabled]))
  const modules = Object.fromEntries(
    MODULE_KEYS.map((key) => {
      const flag = enabled.has(key) ? key : MODULE_FLAGS[key]
      return [key, flag === null ? true : enabled.get(flag) === true]
    }),
  ) as Record<ModuleKey, boolean>
  return { modules }
}

/**
 * The shell's badge: how current the website's own commerce data is, from the
 * cheapest Overview route (the trend, about 15 ms against the KPIs' 250) over
 * the API's default scope — the last 28 days, production — by week, so the
 * answer stays a handful of buckets. The top bar polls it every minute.
 */
export const FRESHNESS_PATH = '/overview/revenue-trend?granularity=week'

export function freshnessFromWire(body: RevenueTrendOut): DataMeta {
  return { source: 'live', lastUpdatedAt: body.meta.freshness.latest_source_updated_at }
}

